import styled, { css } from 'styled-components';
import type { MergedThemeColorsScheme } from '../../theme/types';
import { mobile } from '../mixins/media';
import type { ColorVariants } from './index';

interface CommonProps {
    $margin?: string;
    $mobileMargin?: string;
    $padding?: string;
    $mobilePadding?: string;
    $block?: boolean;
    $color?: string;
    $textAlign?: string;
    $colorVariant?: ColorVariants;
    $textOverflowDisabled?: boolean;
}

const colorVariantMap: Record<ColorVariants, (colors: MergedThemeColorsScheme) => string> = {
    base: colors => colors.basicColors['90%'],
    second: colors => colors.basicColors['50%'],
    disabled: colors => colors.basicColors['25%'],
    colored: colors => colors.accent.textOrIcon,
    danger: colors => colors.system.dangerDefault,
};

const commonStyles = css<CommonProps>`
    font-style: normal;
    font-weight: normal;
    text-align: ${props => props.$textAlign};
    margin: ${props => props.$margin || 0};
    padding: ${props => props.$padding || 0};
    ${props =>
        props.$block &&
        css`
            display: block;
        `};
    ${props =>
        (props.$color || props.$colorVariant) &&
        css`
            color: ${props.$color ||
            (props.$colorVariant && colorVariantMap[props.$colorVariant](props.theme.root.colors))};
        `};

    ${props =>
        props.$textOverflowDisabled &&
        css`
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
        `};

    ${mobile<CommonProps>`
        margin: ${(props: CommonProps) => props.$mobileMargin || props.$margin || 0};
        padding: ${(props: CommonProps) => props.$mobilePadding || props.$padding || 0};
    `}
`;

const title1Styles = css`
    font-size: 96px;
    line-height: 120px;
    letter-spacing: -1.5px;
`;
const title2Styles = css`
    font-weight: bold;
    font-size: 64px;
    line-height: 84px;
    letter-spacing: -1px;
`;
const title3Styles = css`
    font-weight: bold;
    font-size: 54px;
    line-height: 80px;
    letter-spacing: -0.5px;
`;
const title4Styles = css`
    font-weight: 500;
    font-size: 48px;
    line-height: 56px;
`;
const title42Styles = css`
    font-style: normal;
    font-weight: 500;
    font-size: 40px;
    line-height: 40px;
    letter-spacing: 0.25px;
`;
const title5Styles = css`
    font-weight: bold;
    font-size: 32px;
    line-height: 40px;
    letter-spacing: 0.25px;
`;
const title5BStyles = css`
    font-weight: 700;
    font-size: 20px;
    line-height: 28px;
`;
const title6Styles = css`
    font-weight: bold;
    font-size: 24px;
    line-height: 32px;
`;
const title7Styles = css`
    font-weight: 500;
    font-size: 24px;
    line-height: 28px;
`;
const title8Styles = css`
    font-weight: normal;
    font-size: 20px;
    line-height: 30px;
    letter-spacing: 0.15px;
`;
const title9Styles = css`
    font-weight: 500;
    font-size: 20px;
    line-height: 24px;
    letter-spacing: 0.15px;
`;
const body1Styles = css`
    font-weight: bold;
    font-size: 18px;
    line-height: 24px;
    letter-spacing: 0.25px;
`;
const body1RStyles = css`
    font-weight: normal;
    font-size: 16px;
    line-height: 20px;
`;
const body2Styles = css`
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.1px;
`;
const body2BStyles = css`
    font-weight: 700;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0.2px;
`;
const body3Styles = css`
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.1px;
`;
const body3MStyles = css`
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
`;
const body3UppercaseStyles = css`
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.1px;
    text-transform: uppercase;
`;
const body4Styles = css`
    font-size: 14px;
    line-height: 24px;
`;
const bodyTextStyles = css`
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.1px;
`;
const body4MStyles = css`
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
`;
const body5Styles = css`
    font-weight: normal;
    font-size: 14px;
    line-height: 16px;
`;
const buttonStyles = css`
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.25px;
`;
const subtitle1Styles = css`
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    text-transform: uppercase;
`;
const subtitle2Styles = css`
    font-weight: bold;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.25px;
`;
const subtitle3Styles = css`
    font-weight: normal;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.25px;
`;
const subtitle4Styles = css`
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.25px;
`;
const subtitle5Styles = css`
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;
    letter-spacing: 0px;
`;
const caption1Styles = css`
    font-size: 12px;
    line-height: 14px;
    letter-spacing: 0.14px;
`;
const caption2Styles = css`
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.14px;
`;
const caption2RStyles = css`
    font-size: 12px;
    line-height: 16px;
`;
const caption3Styles = css`
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.14px;
`;
const caption4Styles = css`
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
`;

const caption5Styles = css`
    font-weight: bold;
    font-size: 12px;
    line-height: 140%;
    letter-spacing: 0.15px;
`;
const subcaptionRStyles = css`
    font-size: 11px;
    line-height: 13px;
`;
const subhead2RStyles = css`
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
`;
const subhead3Styles = css`
    font-weight: 400;
    font-size: 13px;
    line-height: 16px;
`;
const text1Styles = css`
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0.25px;
`;
const text2Styles = css`
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0.25px;
`;
const text3Styles = css`
    font-weight: 500;
    font-size: 15px;
    line-height: 20px;
    letter-spacing: 0.25px;
`;

const messageInputStyles = css`
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.25px;
`;

const messageStyles = css`
    font-weight: normal;
    font-size: 18px;
    line-height: 22px;
`;

const message14RStyles = css`
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
`;

const message14Styles = css`
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
`;

const message15Styles = css`
    font-size: 15px;
    font-weight: 400;
    line-height: 19px;
`;

const message16Styles = css`
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
`;

const message17Styles = css`
    font-size: 17px;
    font-weight: 400;
    line-height: 21px;
`;

const message18Styles = css`
    font-size: 18px;
    font-weight: 400;
    line-height: 22px;
`;

const message19Styles = css`
    font-size: 19px;
    font-weight: 400;
    line-height: 23px;
`;

const additionalForMessageM13Styles = css`
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    letter-spacing: 0.15px;
`;

const additionalForMessageRStyles = css`
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.15px;
`;

const additionalForMessageR13Styles = css`
    font-size: 13px;
    line-height: 18px;
    font-weight: 400;
    letter-spacing: 0.15px;
`;

const fat1Styles = css`
    //font-family: Rubik;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0.25px;
`;

export const Title1 = styled.h1<CommonProps>`
    ${commonStyles}
    ${title1Styles}
`;

export const Title2 = styled.h2<CommonProps>`
    ${commonStyles}
    ${title2Styles}
`;

export const Title3 = styled.h3<CommonProps>`
    ${commonStyles}
    ${title3Styles}
`;

export const Title4 = styled.h4<CommonProps>`
    ${commonStyles}
    ${title4Styles}
`;

export const Title5 = styled.h5<CommonProps>`
    ${commonStyles}
    ${title5Styles}
`;

export const Title5B = styled.h5<CommonProps>`
    ${commonStyles}
    ${title5BStyles}
`;

export const Title6 = styled.h6<CommonProps>`
    ${commonStyles}
    ${title6Styles}
`;

export const Title7 = styled.span<CommonProps>`
    ${commonStyles}
    ${title7Styles}
`;
export const Title8 = styled.span<CommonProps>`
    ${commonStyles}
    ${title8Styles}
`;
export const Title9 = styled.span<CommonProps>`
    ${commonStyles}
    ${title9Styles}
`;

export const Body1 = styled.div<CommonProps>`
    ${commonStyles}
    ${body1Styles}
`;

export const Body1R = styled.div<CommonProps>`
    ${commonStyles}
    ${body1RStyles}
`;

export const Body2 = styled.div<CommonProps>`
    ${commonStyles}
    ${body2Styles}
`;

export const Body2B = styled.div<CommonProps>`
    ${commonStyles}
    ${body2BStyles}
`;

export const Body3 = styled.div<CommonProps>`
    ${commonStyles}
    ${body3Styles}
`;

export const Body3M = styled.div<CommonProps>`
    ${commonStyles}
    ${body3MStyles}
`;

export const Body3Uppercase = styled.div<CommonProps>`
    ${commonStyles}
    ${body3UppercaseStyles}
`;

export const Body4 = styled.div<CommonProps>`
    ${commonStyles}
    ${body4Styles}
`;

export const Body4Text = styled.div<CommonProps>`
    ${commonStyles}
    ${bodyTextStyles}
`;

export const Body4M = styled.span<CommonProps>`
    ${commonStyles}
    ${body4MStyles}
`;

export const Body5 = styled.span<CommonProps>`
    ${commonStyles}
    ${body5Styles}
`;

export const Button = styled.span<CommonProps>`
    ${commonStyles}
    ${buttonStyles}
`;

export const Subtitle1 = styled.span<CommonProps>`
    ${commonStyles}
    ${subtitle1Styles}
`;

export const Subtitle2 = styled.span<CommonProps>`
    ${commonStyles}
    ${subtitle2Styles}
`;

export const Subtitle3 = styled.span<CommonProps>`
    ${commonStyles}
    ${subtitle3Styles}
`;

export const Subtitle4 = styled.span<CommonProps>`
    ${commonStyles}
    ${subtitle4Styles}
`;

export const Subtitle5 = styled.span<CommonProps>`
    ${commonStyles}
    ${subtitle5Styles}
`;

export const Caption1 = styled.span<CommonProps>`
    ${commonStyles}
    ${caption1Styles}
`;

export const Caption2 = styled.span<CommonProps>`
    ${commonStyles}
    ${caption2Styles}
`;

export const Caption2R = styled.span<CommonProps>`
    ${commonStyles}
    ${caption2RStyles}
`;

export const Caption3 = styled.span<CommonProps>`
    ${commonStyles}
    ${caption3Styles}
`;

export const Subhead2R = styled.span<CommonProps>`
    ${commonStyles}
    ${subhead2RStyles}
`;

export const Subhead3 = styled.span<CommonProps>`
    ${commonStyles}
    ${subhead3Styles}
`;

export const Caption4 = styled.span<CommonProps>`
    ${commonStyles}
    ${caption4Styles}
`;

export const Caption5 = styled.span<CommonProps>`
    ${commonStyles}
    ${caption5Styles}
`;

export const SubCaptionR = styled.span<CommonProps>`
    ${commonStyles}
    ${subcaptionRStyles}
`;

export const Text1 = styled.span<CommonProps>`
    ${commonStyles}
    ${text1Styles}
`;

export const Text2 = styled.span<CommonProps>`
    ${commonStyles}
    ${text2Styles}
`;

export const Text3 = styled.span<CommonProps>`
    ${commonStyles}
    ${text3Styles}
`;

export const MessageInput = styled.span<CommonProps>`
    ${commonStyles}
    ${messageInputStyles}
`;

export const Message = styled.span<CommonProps>`
    ${commonStyles}
    ${messageStyles}
`;

export const Message14 = styled.span<CommonProps>`
    ${commonStyles}
    ${message14Styles}
`;

export const Message15 = styled.span<CommonProps>`
    ${commonStyles}
    ${message15Styles}
`;

export const Message16 = styled.span<CommonProps>`
    ${commonStyles}
    ${message16Styles}
`;

export const Message17 = styled.span<CommonProps>`
    ${commonStyles}
    ${message17Styles}
`;

export const Message18 = styled.span<CommonProps>`
    ${commonStyles}
    ${message18Styles}
`;

export const Message19 = styled.span<CommonProps>`
    ${commonStyles}
    ${message19Styles}
`;

export const Message14R = styled.span<CommonProps>`
    ${commonStyles}
    ${message14RStyles}
`;

export const AdditionalForMessageM13 = styled.span<CommonProps>`
    ${commonStyles}
    ${additionalForMessageM13Styles}
`;

export const AdditionalForMessageR = styled.span<CommonProps>`
    ${commonStyles}
    ${additionalForMessageRStyles}
`;

export const AdditionalForMessageR13 = styled.span<CommonProps>`
    ${commonStyles}
    ${additionalForMessageR13Styles}
`;

export const Title42 = styled.h4<CommonProps>`
    ${commonStyles}
    ${title42Styles}
`;

export const Fat1 = styled.span<CommonProps>`
    ${commonStyles}
    ${fat1Styles};
`;
